import React from 'react';
import { BookingImages } from 'acscp/store';
import { getViewportHeight, getViewportWidth } from 'acscp/components/common/helpers/viewportHelpers';
import debounce from 'lodash.debounce';
import ReactModal from 'react-modal';
import { QuoteImageGallery, QuoteImageGalleryProps } from 'acscp/components/quotes/quoteImageGallery';
import style from './quoteImageGalleryFullscreen.module.scss';
import { unlockScreenRotation } from 'acscp/components/common/unlockScreenRotation';
import imageGalleryStyle from 'acscp/components/common/imageGallery.module.scss';

/**
 * Quote image gallery displayed in a modal with a darkened background i.e. in 'fullscreen' mode.
 *
 * Note: this component relies heavily on knowledge about QuoteImageGallery, ImageGallery, ReactModal, and CroppedImage
 * components implementations to correctly size and display the gallery.
 * This is important for displaying the gallery on all phones in landscape mode and on ultra-wide monitors.
 */
class QuoteImageGalleryFullscreenBase
    extends React.Component<QuoteImageGalleryProps, {contentWidth: string}> {

    constructor(props: QuoteImageGalleryProps) {
        super(props);
        this.state = {contentWidth: ''};
    }

    private resize = () => {
        window.requestAnimationFrame(() => {
            const modalContentContainer = document.querySelector('.' + style.QuoteImageGalleryFullscreen)!;
            const modalContent = modalContentContainer.children[0] as HTMLElement;

            const modalIsConstrained = this.state.contentWidth !== '';
            const notEnoughHorizontalSpace = getViewportWidth() < Math.ceil(modalContent.clientWidth);
            const availableVerticalSpace = getViewportHeight() > Math.ceil(modalContent.clientHeight);

            if (modalIsConstrained &&
                (notEnoughHorizontalSpace || availableVerticalSpace)) {
                this.setState(
                    {contentWidth: ''},
                    () => { this.forceGalleryRerender(); this.resize(); }
                );
                return;
            }

            const notEnoughVerticalSpace = getViewportHeight() < Math.ceil(modalContent.clientHeight);
            if (notEnoughVerticalSpace) {
                /**
                 * QuoteImageGallery shows uses CroppedImages to keep consistent height/width ratio.
                 * As a side effect, the only way to set a desired height for the gallery is
                 * to set a proportional width.
                 *
                 * Unfortunately, QuoteImageGallery shows a fixed height title bar,
                 * which complicates the calculations further.
                 */
                const titleBarHeight = document.querySelector('.' + imageGalleryStyle.Title)!.clientHeight;
                const targetImageHeight = getViewportHeight() - titleBarHeight;
                const currentImageHeight = modalContent.clientHeight - titleBarHeight;
                const widthProportionalChange = targetImageHeight / currentImageHeight;
                const maxWidthAllowedByHeight = modalContentContainer.clientWidth * widthProportionalChange;

                // In certain window resize cases (e.g. screen orientation change),
                // viewport height might allow setting the content width to a value larger than the viewport width.
                // Don't allow that.
                const newContentWidth = Math.min(maxWidthAllowedByHeight, getViewportWidth());
                this.setState(
                    {contentWidth: newContentWidth + 'px'},
                    this.forceGalleryRerender
                );
            }
        });
    }

    /**
     * Trigger additional re-render and recalculation of the image gallery (by re-rendering the whole component)
     * to correctly respond to width changes of the modal container.
     */
    private forceGalleryRerender = () => this.forceUpdate();

    private windowResizeHandler: () => void = debounce(this.resize, 150);

    private verticalPosition: number;
    componentWillMount(): void {
        this.verticalPosition = window.scrollY;
    }

    componentDidMount(): void {
        this.resize();
        window.addEventListener('resize', this.windowResizeHandler);
    }
    componentWillUnmount(): void {
        window.removeEventListener('resize', this.windowResizeHandler);

        setTimeout(() => window.scrollTo(0, this.verticalPosition), 0);
    }

    render(): JSX.Element {
        return (
            <ReactModal
                className={style.QuoteImageGalleryFullscreen}
                overlayClassName={style.QuoteImageGalleryFullscreenOverlay}
                contentLabel="imageGallery"
                isOpen={true}
                onRequestClose={this.props.onClose}
                style={{content: {width: this.state.contentWidth}}}
            >
                <QuoteImageGallery {...this.props} />
            </ReactModal>
        );
    }
}

const QuoteImageGalleryFullscreen = LOCK_ORIENTATION
    ? unlockScreenRotation(QuoteImageGalleryFullscreenBase)
    : QuoteImageGalleryFullscreenBase;

export { QuoteImageGalleryFullscreen };
